'use client'

import { AnimatedSection } from '@/ui/animated-section'
import { isMediaKind, type MediaKind } from '@/entries'
import type { EnrichedMediaResult } from '@/enrichment'
import type { LabelSuggestion } from '../media-preview/types'
import type { PersonWithLastInteraction } from '@/entries'
import { MediaForm } from '../forms/MediaForm'
import type { FormStateReturn } from '../hooks/useFormState'
import type { FormHandlers } from '../hooks/useFormHandlers'

interface FormMediaSectionProps {
  formState: FormStateReturn
  handlers: FormHandlers
  hasContent: boolean
  isMediaDetected: boolean
  aiDetectedType: string | null
  isAnalyzing: boolean
  enrichedData: EnrichedMediaResult | null
  isEnriching: boolean
  enrichmentError: string | null
  retryEnrichment: () => void
  selectedPeople: PersonWithLastInteraction[]
  availablePeople: PersonWithLastInteraction[]
  isPeopleLoading: boolean
  labelSuggestions: LabelSuggestion[]
  isCheckingLabels: boolean
  toggleLabelSuggestion: (id: string) => void
  isLoading: boolean
}

/**
 * Media detection + manual entry form section.
 */
export function FormMediaSection({
  formState,
  handlers,
  hasContent,
  isMediaDetected,
  aiDetectedType,
  isAnalyzing,
  enrichedData,
  isEnriching,
  enrichmentError,
  retryEnrichment,
  selectedPeople,
  availablePeople,
  isPeopleLoading,
  labelSuggestions,
  isCheckingLabels,
  toggleLabelSuggestion,
  isLoading,
}: FormMediaSectionProps) {
  const showMedia =
    hasContent &&
    isMediaDetected &&
    !!aiDetectedType &&
    isMediaKind(aiDetectedType) &&
    !isAnalyzing

  return (
    <AnimatedSection show={showMedia}>
      {showMedia && aiDetectedType && isMediaKind(aiDetectedType) && (
        <MediaForm
          aiDetectedType={aiDetectedType as MediaKind}
          enrichedData={enrichedData}
          isEnriching={isEnriching}
          enrichmentError={enrichmentError}
          showManualForm={formState.showManualMediaForm}
          dismissedMediaType={formState.dismissedMediaType}
          editedMediaValues={formState.editedMediaValues}
          genresInputValue={formState.genresInputValue}
          selectedPeople={selectedPeople}
          availablePeople={availablePeople}
          isPeopleLoading={isPeopleLoading}
          labelSuggestions={labelSuggestions}
          isCheckingLabels={isCheckingLabels}
          isSubmitting={isLoading}
          onConfirm={handlers.handleMediaSubmit}
          onDismiss={handlers.handleMediaDismiss}
          onRetry={retryEnrichment}
          onSelectAlternative={handlers.handleSelectAlternative}
          onAddPerson={handlers.handleAddPerson}
          onRemovePerson={handlers.handleRemovePerson}
          onAddNewPerson={() => formState.setShowAddPerson(true)}
          onToggleLabelSuggestion={toggleLabelSuggestion}
          onEditChange={formState.setEditedMediaValues}
          onGenresInputChange={formState.setGenresInputValue}
          onManualSubmit={handlers.handleManualMediaSubmit}
          onManualCancel={handlers.handleManualMediaCancel}
        />
      )}
    </AnimatedSection>
  )
}
